import { useState } from 'react';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, Typography } from '@mui/material';
import moment from 'moment';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import { ConfirmFollowUpActionModal } from './ConfirmFollowUpActionModal';
import { FollowUpActionForm, FollowUpActionMode, FollowUpActionSchema } from './FollowUpActionForm';

//this can change
export type FollowUpActionType = z.infer<typeof FollowUpActionSchema> & { temp_id?: string; delete?: boolean };

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: `${spacing(3)} ${spacing(2)}`,
            textTransform: 'none',
            backgroundColor: 'white'
        },
        assignContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(13), //if we have many fuas put margin so that the bottom bar doesn't block it
            padding: spacing(2),
            display: 'flex',
            flexDirection: 'column'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        addFUAButton: {
            textTransform: 'none',
            paddingLeft: spacing(2),
            alignSelf: 'flex-start'
        },
        fuasListContainer: {
            margin: `${spacing(1)} ${spacing(2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        }
    };
});

interface FollowUpActionsProps {
    onGoBack: (followUpActions: FollowUpActionType[]) => void;
    onSubmit: (followUpActions: FollowUpActionType[]) => void;
    initialFollowUpActions: FollowUpActionType[];
}

export const FollowUpActions = (props: FollowUpActionsProps) => {
    const { onSubmit, onGoBack, initialFollowUpActions } = props;
    const { classes } = useStyles();

    const [followUpActions, setFollowUpActions] = useState<FollowUpActionType[]>(initialFollowUpActions);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showAddFollowUp, setShowAddFollowUp] = useState(false);
    const [mode, setMode] = useState<FollowUpActionMode>('add');
    const [initialFollowUpAction, setInitialFollowUpAction] = useState<z.infer<typeof FollowUpActionSchema>>();

    const handleOpenConfirmation = () => {
        setShowConfirmation(true);
    };

    const handleCloseConfirmation = () => {
        setShowConfirmation(false);
    };

    const handleSubmit = () => {
        setShowConfirmation(false);
        onSubmit(followUpActions);
    };

    const handleEditFollowUpAction = (followUpAction: z.infer<typeof FollowUpActionSchema>) => {
        setInitialFollowUpAction(followUpAction);
        setMode('edit');
        setShowAddFollowUp(true);
    };

    const handleAddFollowUpAction = () => {
        setInitialFollowUpAction(null);
        setMode('add');
        setShowAddFollowUp(true);
    };

    const handleFollowUpAction = (followUpAction: FollowUpActionType) => {
        if (mode === 'add') {
            const newAction = { ...followUpAction, temp_id: crypto.randomUUID() };
            setFollowUpActions((prev) => [...prev, newAction]);
        } else if (followUpAction.delete) {
            setFollowUpActions((prev) => prev.filter((item) => item.temp_id !== followUpAction.temp_id));
        } else {
            setFollowUpActions((prev) =>
                prev.map((el) => (el.temp_id === followUpAction.temp_id ? { ...el, ...followUpAction } : el))
            );
        }
        setShowAddFollowUp(false);
    };

    return (
        <>
            <PageLayout>
                {{
                    appBar: (
                        <AppBar
                            title={_t('mobile_mobileform_follow_up_actions')}
                            onGoBack={() => onGoBack(followUpActions)}
                        />
                    ),
                    content: (
                        <div className={classes.assignContainer}>
                            <Typography sx={{ fontWeight: 'medium', color: 'black' }} variant="subtitle1">
                                {_t('mobile_mobileform_follow_up_actions_assign_title')}
                            </Typography>
                            <Typography sx={{ fontWeight: 'normal', marginBottom: 4 }} variant="caption">
                                {_t('mobile_mobileform_follow_up_actions_assign_subtitle')}
                            </Typography>

                            {followUpActions.map((followUpAction) => {
                                const moment_date = moment(followUpAction.due_date)
                                    .tz(moment.tz.guess())
                                    .format('ddd D, MMM YYYY, h:mma z');
                                return (
                                    <Box
                                        key={followUpAction.title}
                                        className={classes.fuasListContainer}
                                        onClick={() => handleEditFollowUpAction(followUpAction)}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{ fontWeight: 'normal', color: 'black' }}
                                                variant="subtitle1"
                                            >
                                                {followUpAction.title}
                                            </Typography>
                                            <Typography sx={{ fontWeight: 'normal' }} variant="body2">
                                                {moment_date}
                                            </Typography>
                                        </Box>
                                        <KeyboardArrowRightIcon sx={{ color: '#1976d2', fontSize: 30 }} />
                                    </Box>
                                );
                            })}

                            <Button
                                className={classes.addFUAButton}
                                sx={{ marginTop: followUpActions.length > 0 ? 4 : 0 }}
                                onClick={handleAddFollowUpAction}
                            >
                                {_t('mobile_mobileform_follow_up_actions_add_button')}
                            </Button>
                        </div>
                    ),
                    bottomBar: (
                        <BottomBar>
                            <div className={classes.bottomBar}>
                                <Button
                                    className={classes.bottomButton}
                                    disabled={followUpActions.length === 0}
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmit}
                                >
                                    {_t('mobile_mobileform_follow_up_actions_assign_button')}
                                </Button>
                                <Button
                                    className={classes.bottomButton}
                                    disabled={followUpActions.length > 0}
                                    onClick={handleOpenConfirmation}
                                >
                                    {_t('mobile_mobileform_follow_up_actions_without_assign_button')}
                                </Button>
                            </div>
                        </BottomBar>
                    )
                }}
            </PageLayout>

            <FollowUpActionForm
                open={showAddFollowUp}
                onGoBack={() => setShowAddFollowUp(false)}
                mode={mode}
                initialFollowUpAction={initialFollowUpAction}
                handleFollowUpAction={handleFollowUpAction}
            />

            <ConfirmFollowUpActionModal
                onSubmit={handleSubmit}
                showConfirmation={showConfirmation}
                handleCloseConfirmation={handleCloseConfirmation}
                _t={_t}
            />
        </>
    );
};
